// Import necessary dependencies
import Handsontable from 'handsontable';
import angular from 'angular';
import he from 'he';
import $ from 'jquery';
import RichTextEditorService from '../../services/core/RichTextEditorService'

// After Handsontable.unlisten() releases keyboard listening, the current Tab keydown has already been consumed by CKEditor's key handler
const TABBABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function focusElementAfter(referenceEl, isShift) {
  // Filter out descendants of the wrapper so we land on the next focusable AFTER the table, not on an MCQ input inside it.
  const tabbables = Array.from(document.querySelectorAll(TABBABLE_SELECTOR))
    .filter(el => el === referenceEl || !referenceEl.contains(el));
  const idx = tabbables.indexOf(referenceEl);
  if (idx === -1) return;
  const next = isShift ? tabbables[idx - 1] : tabbables[idx + 1];
  if (next) next.focus();
  else referenceEl.blur();
}

// Define the RichTextEditor class
class HandsontableRichtextEditor extends Handsontable.editors.BaseEditor {
  constructor(instance) {
    super(instance);

    // Inject AngularJS services
    const injector = angular.element(document.body).injector();
    this.$timeout = injector.get('$timeout');
    this.$translate = injector.get('$translate');
    this.$config = injector.get('$config');

    this.RichTextEditorService = injector.instantiate(RichTextEditorService);

    this.CURRENT_EDITOR_NAME = '';
    this.keyPressCharBuffer = null;
    this.sft = 0;
    this._opened = false;

    // Pre-bind hook handlers so add/remove receive the same reference.
    this.handleBeforeKeyDown = this.handleBeforeKeyDown.bind(this);
    this.afterPaste = this.afterPaste.bind(this);
    this.afterSelectionEnd = this.afterSelectionEnd.bind(this);

    this.bindListeners();
  }

  // Bind Handsontable hooks to catch a few events
  bindListeners() {
    this.unbindListeners();
    Handsontable.hooks.add('beforeKeyDown', this.handleBeforeKeyDown, this.instance);
    Handsontable.hooks.add('afterPaste', this.afterPaste, this.instance);
    Handsontable.hooks.add('afterSelectionEnd', this.afterSelectionEnd, this.instance);
  }

  // Unbind Handsontable hooks
  unbindListeners() {
    Handsontable.hooks.remove('beforeKeyDown', this.handleBeforeKeyDown, this.instance);
    Handsontable.hooks.remove('afterPaste', this.afterPaste, this.instance);
    Handsontable.hooks.remove('afterSelectionEnd', this.afterSelectionEnd, this.instance);
  }

  focus() {
    console.log('HandsontableRichtextEditor: focus');
    const editor = CKEDITOR.instances[this.CURRENT_EDITOR_NAME];
    if (!editor || !editor.editable()) {
      return;
    }
    try {
      editor.focus();
    } catch (e) {
      console.warn('HandsontableRichtextEditor: focus failed', e);
    }
  }

  // Handle before key down event
  handleBeforeKeyDown(event) {

    if (this.instance.getActiveEditor() !== this) {
      return;
    }

    if (this.isInReadOnlyCell()) {
      // Focus on MCQ input elements if in read-only cell
      this.focusMCQ();
      return;
    }

    const keyBufferDebug = false;

    switch (parseInt(event.keyCode)) {
      case 8: // BACKSPACE
      case 13: // ENTER
        this.instance.getActiveEditor().beginEditing();
        event.stopImmediatePropagation();
        break;
      case 9: // TAB
        // If editor.on('key') already handled this TAB (it set the flag
        // before queuing pendingTab + finishEditing), don't call
        // beginEditing here — that would re-open the editor on the current
        // cell and clobber the navigation. By the time we get here,
        // this._opened is already false because finishEditing ran, so a
        // _opened-based guard wouldn't work; the explicit flag does.
        if (this._tabFromOpenEditor) {
          this._tabFromOpenEditor = false;
        } else {
          this.instance.getActiveEditor().beginEditing();
        }
        event.stopImmediatePropagation();
        break;
      case 27: // ESCAPE
      case 38: // UP-ARROW
      case 40: // DOWN-ARROW
      case 37: // LEFT-ARROW
      case 39: // RIGHT-ARROW
        break;
      default:
        this.appendKeyToKeyBuffer(event);
    }
  }

  // Handle after paste event
  afterPaste() {
    this.instance.getActiveEditor().beginEditing();
  }

  // Reset pre-edit char buffer when selection moves. Without this, characters
  // typed while one cell was selected would phantom-prepend to a later edit on a different cell
  afterSelectionEnd() {
    this.keyPressCharBuffer = null;
  }

  // Check if we are in a read-only cell
  isInReadOnlyCell(rowParam, colParam) {
    const row = rowParam !== undefined ? rowParam : this.row;
    const col = colParam !== undefined ? colParam : this.col;
    const cellMeta = this.instance.getCellMeta(row, col);
    return cellMeta.readOnly === true;
  }

  // Focus first MCQ input element in current cell
  focusMCQ() {
    if ($(this.TD).find('input').length > 0) {
      // Deselect and stop listening to input in table
      this.instance.deselectCell();
      this.instance.unlisten();

      const allInputs = $(this.TD).find('input');
      const firstInput = allInputs[0];
      const lastInput = allInputs[allInputs.length - 1];

      // Put input focus into the first element
      firstInput.focus();

      // Handle keydown event on the last input
      $(lastInput).keydown((kd) => {
        if (parseInt(kd.keyCode) !== 9) {
          return;
        }

        $(lastInput).off('keydown');
        $(lastInput).blur();

        kd.preventDefault();
        kd.stopPropagation();

        this.instance.listen();

        // Determine which next row/col to jump to
        let nextCol = this.col + 1;
        let nextRow = this.row;

        if (nextCol > this.instance.countCols() - 1) {
          nextCol = 0;
          nextRow++;
        }

        // Select next cell
        this.instance.selectCell(nextRow, nextCol);
      });
    }
  }

  // Append character to key buffer
  appendKeyToKeyBuffer(event) {
    const inp = event.key;
    if (/^[A-Za-z0-9_äÄöÖüÜß]$/.test(inp)) {
      const encodedChar = he.encode(inp);
      if (typeof this.keyPressCharBuffer === 'string' && this.keyPressCharBuffer.length > 0) {
        this.keyPressCharBuffer += encodedChar;
      } else {
        this.keyPressCharBuffer = encodedChar;
      }
    }
  }

  // Open the editor
  open() {
    if (this.isInReadOnlyCell()) {
      this.focusMCQ();
      return;
    }

    this._opened = true;

    const self = this;
    const selected = this.instance.getSelected();
    const row = this.row;
    const col = this.col;
    const TD = this.TD;
    const cellData = this.instance.getDataAtCell(row, col);
    let cellValue = cellData ? cellData : '';

    const useHtmlRenderSanitize =
      this.isRichTextEnabled() || this.isInReadOnlyCell() || cellValue.indexOf('input') !== -1 || cellValue.indexOf('select') !== -1;
    if (useHtmlRenderSanitize) {
      cellValue = this.instance.getSettings().htmlRendererSanitizeInstance.sanitizeHtml(cellValue);
    } else {
      cellValue = this.instance.getSettings().textRendererSanitizeInstance.sanitizeHtml(cellValue);
    }

    const editorName = `hotEditor_${this.instance.guid}_${row}_${col}_${++HandsontableRichtextEditor._instanceCounter}`;

    // Defensively destroy any leftover CKEditor instance for the previous
    // editor name. The actual data save happens earlier, in finishEditing —
    // by the time we get here, the previous editor's content has often
    // already been wiped by Handsontable's lifecycle.
    const prevEditorName = this.CURRENT_EDITOR_NAME;
    if (prevEditorName && prevEditorName !== editorName && CKEDITOR.instances[prevEditorName]) {
      try { CKEDITOR.instances[prevEditorName].destroy(); } catch (_) {}
      delete CKEDITOR.instances[prevEditorName];
    }

    this.CURRENT_EDITOR_NAME = editorName;

    // Remove any leftover editor divs in this table (now safe — content was
    // either persisted above, or these are stale aborted-open shells).
    $(`#${this.instance.guid} .handsontableRichText`).remove();

    // Append CKEditor container
    let editorClasses = 'handsontableRichText';
    if (this.instance.getSettings().langISO === 'ur_UR') {
      editorClasses += ' urdu-font-force';
    }
    $(`#${this.instance.guid} .ht_master .wtHolder`).append(
      `<div id="${editorName}" name="${editorName}" contenteditable="true" class="${editorClasses}">${cellValue}</div>`
    );

    // Initialize CKEditor
    const RTEService = this.RichTextEditorService;
    let config = {};

    // Configure CKEditor based on candidatePOV
    if (this.instance.getSettings().candidatePOV === false) {
      const ibToolBar = [
        ['Bold', 'Italic', '-', 'Undo', 'Redo'],
        ['Underline', 'Subscript', 'Superscript'],
        ['SpecialChar', 'SpecialCharSetting', 'WirisEditor'],
        ['Checkbox', 'Radio', 'Select', 'TextField'],
      ];
      config = RTEService.init(RTEService.OBJECT_TYPE.MCQ);
      RTEService.set('toolbar', ibToolBar);
      RTEService.addPlugin('specialcharsetting');
    } else {
      const ibToolBar = [
        ['Bold', 'Italic', '-', 'Undo', 'Redo'],
        ['Underline', 'Subscript', 'Superscript'],
        ['SpecialChar', 'WirisEditor'],
      ];
      config = RTEService.init(RTEService.OBJECT_TYPE.TEXT);
      RTEService.set('toolbar', ibToolBar);
    }

    RTEService.initLanguage(!this.instance.getSettings().candidatePOV, false);

    let specialChars = this.instance.getSettings().contentObject.getPreferences().specialChars;
    if (!specialChars || !angular.isArray(specialChars)) {
      specialChars = ['&#x3b1;', '&#x3b2;', '&#x3b3;', '&#x3bb;', '&#x3bc;', '&#x3c1;', '&#x3a9;', '&#x3c0;', '&#x2211;', '&#x2206;', '&#x3b8;'];
      this.instance.getSettings().contentObject.getPreferences().specialChars = specialChars;
    }
    RTEService.set('specialChars', specialChars);
    RTEService.set('inlineEditor', true);
    RTEService.set('startupFocus', true);
    RTEService.set('autoParagraph', false);
    RTEService.set('ignoreEmptyParagraph', true);
    RTEService.set('enterMode', CKEDITOR.ENTER_P);
    RTEService.set('shiftEnterMode', CKEDITOR.ENTER_P);
    RTEService.set('fillEmptyBlocks', false);
    RTEService.set('inHandsontable', true);
    RTEService.set('guid', this.instance.guid);

    if (this.isRichTextEnabled()) {
      RTEService.addPlugin('sharedspace');
      RTEService.addPlugin('wiriseditor');
    }
    RTEService.removePlugin('image');

    RTEService.set('sharedSpaces', {
      top: `step3-${this.instance.guid}`,
    });
    config = RTEService.getConfig();

    // Get the editor instance
    const editorElement = $(`#${editorName}`).get(0);
    const editor = RTEService.getEditor(editorElement, config);
    editor.tableObjectController = this.instance.getSettings().tableObjectController;

    // Set editor container position
    $(`#${editorName}`).css({
      position: 'absolute',
      zIndex: 10,
      backgroundColor: '#ececec',
      border: '2px solid rgb(82, 146, 247)',
      padding: '0 4px',
      height: $(TD).height() + 2,
      width: $(TD).width() + 2,
      top: $(TD).position().top - (selected && selected[0] === 0 ? 0 : 1),
      left: $(TD).position().left - (selected && selected[1] === 0 ? 0 : 1),
    });

    // Editor instance ready event
    editor.on('instanceReady', function (ev) {
      if (!self.isRichTextEnabled()) {
        $(`#cke_${editorName}`).find('.cke_inner').remove();
        $(`#cke_${editorName}`).remove();
      }
      let timeoutUpdate;

      const setEditorDimensions = function () {
        $(`#${editorName}`).css({
          height: $(TD).innerHeight() + 2,
          width: $(TD).innerWidth() + 2,
          top: $(TD).position().top - (selected && selected[0] === 0 ? 0 : 1),
          left: $(TD).position().left - (selected && selected[1] === 0 ? 0 : 1),
        });
      };

      const setData = async () => {
        if (!self.instance || !self.instance.table) return;
        const data = editor.getData() || '';

        try {
          if (data.includes('math xmlns')) {
            await waitForMathConversion();
          } else {
            self.instance.setDataAtCell(row, col, data, 'editedInRichTextEditor');
          }
        } catch (error) {
          console.error('Error setting data:', error);
        }
      };

      const setToolbarPosition = function () {
        const editorToolbarElement = $(`#cke_${editorName}`);
        $(`#${self.instance.guid}`).css('position', 'relative');
        editorToolbarElement.css({
          position: 'absolute',
          top: -editorToolbarElement.height() - 5,
          left: 0,
          width: $(`#${self.instance.guid}`).width() - 2,
        });
        if (
          editorToolbarElement &&
          editorToolbarElement.offset() &&
          $(document).scrollTop() + 45 > editorToolbarElement.offset().top
        ) {
          editorToolbarElement.css({
            position: 'fixed',
            top: 45,
            left: editorToolbarElement.offset().left,
            zIndex: 11,
          });
        }
      };

      const update = async (force = false) => {
        if (timeoutUpdate) {
          cancelAnimationFrame(timeoutUpdate);
        }

        if (force) {
          try {
            await setData();
            setEditorDimensions();
            if (typeof self.instance.updateTableHeight === 'function') {
              self.instance.updateTableHeight();
            }
            setToolbarPosition();
          } catch (error) {
            console.error('Error during update:', error);
          }
        } else {
          timeoutUpdate = requestAnimationFrame(() => update(true));
        }
      };

      // Per-instance scroll namespace so opening an editor in one mounted
      // table does not detach the toolbar-position handler of any other
      // editor (or any unrelated component listening on document.scroll).
      const scrollNs = `scroll.hotEditor_${self.instance.guid}`;
      $(document).off(scrollNs);
      $(document).on(scrollNs, function () {
        setToolbarPosition();
      });
      $(`#${self.instance.guid} .ht_master .wtHolder`).off('scroll');
      $(`#${self.instance.guid} .ht_master .wtHolder`).on('scroll', function () {
        setEditorDimensions();
      });

      // After Tab inside the editor: either move to the next cell, exit the
      // table to the surrounding tab order, or do nothing (Escape).
      let pendingTab = null;

      // Set data to CKEditor
      const hasValidCellValue = angular.isString(cellValue) && cellValue.trim().length > 0;

      if (self.hasValidKeyBuffer() && !hasValidCellValue) {
        self.overwriteKeyBufferIntoEditor(editor);
      } else {
        self.setCellValueAndAppendKeyBuffer(editor, cellValue);
      }

      // Editor key event
      editor.on('key', function (e) {
        self.keyPressCharBuffer = null;
        update(false);
        const rawKey = parseInt(e.data.keyCode);
        const keyCode = rawKey & 0x00ffff;
        const isShift = !!(rawKey & CKEDITOR.SHIFT);

        switch (keyCode) {
          case 9: { // TAB / SHIFT+TAB
            // Mark this TAB as handled by the editor key path so the later
            // beforeKeyDown hook doesn't call beginEditing on us and
            // re-mount the editor at the current cell, clobbering pendingTab.
            self._tabFromOpenEditor = true;
            update(true);
            const totalRows = self.instance.countRows();
            const totalCols = self.instance.countCols();
            let nextRow = row;
            let nextCol = isShift ? col - 1 : col + 1;
            if (nextCol >= totalCols) { nextRow = row + 1; nextCol = 0; }
            else if (nextCol < 0) { nextRow = row - 1; nextCol = totalCols - 1; }

            if (nextRow >= 0 && nextRow < totalRows) {
              pendingTab = { kind: 'cell', row: nextRow, col: nextCol };
            } else {
              pendingTab = { kind: 'exit', shift: isShift };
            }
            self.finishEditing(true);
            // Explicitly blur the contenteditable so editor.on('blur') →
            // cleanupEditor → pendingTab navigation actually fires. Browsers
            // usually move focus off a contenteditable on TAB by default,
            // but CKEditor's keystroke layer can swallow that, leaving the
            // editor mounted on the current cell with no navigation.
            try { editor.editable().$.blur(); } catch (_) {}
            break;
          }
          case 27: // ESCAPE
            update(true);
            self.finishEditing(true, false, null, true);
            pendingTab = null;
            break;
          case 37: // LEFT
          case 38: // UP
          case 39: // RIGHT
          case 40: { // DOWN
            const native = e.data && (e.data.domEvent || e.data.$);
            const stopPropagationOnly = () => {
              if (!native) return;
              if (typeof native.stopPropagation === 'function') native.stopPropagation();
              if (typeof native.stopImmediatePropagation === 'function') native.stopImmediatePropagation();
              const inner = native.$;
              if (inner && typeof inner.stopImmediatePropagation === 'function') inner.stopImmediatePropagation();
            };

            // Shift+Arrow extends the in-cell text selection — never navigate
            // to an adjacent cell. The boundary check would otherwise return
            // true (the range is still collapsed before the keydown is
            // processed) and incorrectly trigger a cross-cell move.
            if (isShift) {
              stopPropagationOnly();
              break;
            }

            if (!self.isCaretAtBoundary(editor, keyCode)) {
              // In-cell caret movement. Stop propagation so the event doesn't
              // bubble to Handsontable's keyboard manager (which otherwise
              // preventDefaults the arrow and blocks the contenteditable's
              // natural caret advance). DO NOT preventDefault — we still want
              // the browser to move the caret.
              stopPropagationOnly();
              break;
            }
            const totalRows = self.instance.countRows();
            const totalCols = self.instance.countCols();
            let nextRow = row;
            let nextCol = col;
            if (keyCode === 37) nextCol -= 1;
            else if (keyCode === 39) nextCol += 1;
            else if (keyCode === 38) nextRow -= 1;
            else if (keyCode === 40) nextRow += 1;

            // At table edges we still need to consume the event — otherwise
            // Handsontable's default arrow handler wraps the selection (e.g.
            // ArrowRight from the last column lands on the next row's first
            // column) while our editor stays mounted on the original cell,
            // reproducing the exact bug we're fixing. With the event consumed,
            // Excel-style: caret stays put, editor stays open.
            const atTableEdge = nextRow < 0 || nextRow >= totalRows || nextCol < 0 || nextCol >= totalCols;

            // Stop CKEditor's chain AND fully suppress the native keydown
            // (preventDefault + stopPropagation) so neither the browser nor
            // Handsontable acts on the about-to-be-destroyed editor.
            e.cancel();
            if (native) {
              if (typeof native.preventDefault === 'function') native.preventDefault();
            }
            stopPropagationOnly();

            if (atTableEdge) {
              break;
            }

            update(true);
            // beginEditing:false → just move the selection marker, don't reopen
            // an editor in the target cell (matches Excel-style arrow nav and
            // avoids the "editor follows me everywhere" trap).
            pendingTab = { kind: 'cell', row: nextRow, col: nextCol, beginEditing: false };
            self.finishEditing(true);
            // The CKEditor is destroyed by editor.on('blur', ...) → cleanupEditor.
            // Without an explicit blur the contenteditable keeps focus (the arrow
            // key alone doesn't move focus the way Tab does), the blur handler
            // never fires, and the editor stays mounted on the previous cell.
            try { editor.editable().$.blur(); } catch (_) {}
            break;
          }
          default:
            break;
        }
      });

      // Editor change event
      editor.on('change', function () {
        update(false);
      });

      // Editor blur event
      editor.on('blur', async function () {
        if (editor.wirisIsActive) return;

        // If the editor was already destroyed (e.g. open() for a different
        // cell ran first and committed the data itself), this stale blur must
        // NOT save — getData() against a destroyed editor returns "" and
        // would wipe the cell that open() already persisted.
        if (editor.status === 'destroyed' || !CKEDITOR.instances[editorName]) {
          return;
        }

        const currentData = editor.getData();

        if (self.instance.table && currentData !== null && currentData !== undefined) {
          if (!currentData.includes('math xmlns')) {
            self.instance.setDataAtCell(row, col, currentData, 'editedInRichTextEditor');
          }
        }

        try {
          if (currentData?.includes('math xmlns')) {
            await waitForMathConversion();
            await cleanupAndRender();
          } else {
            await cleanupAndRender();
          }
        } catch (error) {
          console.error('Error during editor cleanup:', error);
          cleanupEditor(); // Fallback cleanup
        }
      });

      // Treat the Handsontable instance as gone once .table is null. Calling
      // any Core method after destroy throws "this Handsontable instance has
      // been destroyed" via Core.postMortem — fine when caught, but several
      // call sites here run inside requestAnimationFrame callbacks where an
      // uncaught throw both surfaces in the dev-server overlay AND prevents
      // the surrounding promise from resolving (leaking the await chain).
      const isInstanceAlive = () => !!(self.instance && self.instance.table);

      const waitForMathConversion = () => {
        return new Promise((resolve) => {
          const checkConversion = () => {
            if (!isInstanceAlive()) { resolve(); return; }
            const updatedData = editor.getData();
            const mathElements = $(updatedData).find('math');
            const imgElements = document.querySelectorAll('img[data-mathml]');

            if (!mathElements.length || imgElements.length >= mathElements.length) {
              self.instance.setDataAtCell(row, col, updatedData, 'editedInRichTextEditor');
              resolve();
            } else {
              requestAnimationFrame(checkConversion);
            }
          };

          checkConversion();
        });
      };

      const cleanupAndRender = () => {
        return new Promise((resolve) => {
          requestAnimationFrame(() => {
            try {
              cleanupEditor();
              if (isInstanceAlive()) {
                self.instance.render();
              }
            } catch (err) {
              console.warn('cleanupAndRender failed', err);
            } finally {
              resolve();
            }
          });
        });
      };

      const cleanupEditor = () => {
        // Bail out if a newer open() has already replaced this editor — running
        // the full teardown on a stale closure would nuke the new editor's DOM
        // and detach unrelated listeners.
        if (self.CURRENT_EDITOR_NAME !== editorName) {
          destroyEditor();
          $(`#${editorName}`).remove();
          return;
        }

        $(document).off(scrollNs);
        $(`#${self.instance.guid} .ht_master .wtHolder`).off('scroll');

        if (!self.instance.table) {
          destroyEditor();
          return;
        }

        const finalData = editor.getData();
        if (finalData !== null && finalData !== undefined) {
          self.instance.setDataAtCell(row, col, finalData, 'editedInRichTextEditor');
        }

        self.instance.deselectCell();
        destroyEditor();
        $(`#${editorName}`).remove();
        self.finishEditing(true);

        if (pendingTab) {
          const tab = pendingTab;
          pendingTab = null;
          if (tab.kind === 'cell') {
            self.instance.selectCell(tab.row, tab.col);
            if (tab.beginEditing !== false) {
              const ae = self.instance.getActiveEditor();
              if (ae) ae.beginEditing();
            }
          } else if (tab.kind === 'exit') {
            self.instance.unlisten();
            // closest() survives any future DOM restructuring between the Hot
            // root and the wrapper (vs. hardcoding parentElement).
            const wrapperEl = self.instance.rootElement && self.instance.rootElement.closest('.handsontable-wrapper');
            if (wrapperEl) focusElementAfter(wrapperEl, tab.shift);
          }
        }
      };

      const destroyEditor = () => {
        if (CKEDITOR.instances[editorName]) {
          try {
            editor.destroy();
          } catch (e) {
            console.warn('CKEditor destroy threw', e);
          }
          delete CKEDITOR.instances[editorName];
        }
      };

      if (self.instance.getSettings().candidatePOV === false) {
        editor.on('changeSpecialChars', function () {
          self.instance.getSettings().contentObject.getPreferences().specialChars = editor.config.specialChars;
        });
      }

      // Bail out if a newer open() has already replaced this editor: focusing
      // a stale CKEditor whose editable was just removed corrupts its selection
      // and crashes in CKEditor's setTimeout-driven selection tracking.
      const isStale = self.CURRENT_EDITOR_NAME !== editorName ||
        !document.getElementById(editorName);
      if (isStale) {
        destroyEditor();
        return;
      }

      update(true);
      editor.focus();
      self.instance.getActiveEditor().state = 'STATE_EDITING';

      if (hasValidCellValue) {
        self.goToLastPosition(editor);
      }
    });
  }

  // Close the editor
  close() {
    this._opened = false;
    // ... any other close logic you want to add ...
  }

  // Get value from the editor
  getValue() {
    return $.trim($(`#${this.CURRENT_EDITOR_NAME}`).html());
  }

  // Set value to the editor
  setValue(value) {
    $(`#${this.CURRENT_EDITOR_NAME}`).html($.trim(value));
  }

  // Begin editing
  beginEditing(initialValue) {
    if (this.isInReadOnlyCell()) {
      this.focusMCQ();
      return;
    }

    super.beginEditing(...arguments);
    this.state = 'STATE_WAITING';
  }

  // Finish editing
  finishEditing(isCancelled, ctrlDown, callback, forceReselectCellParam) {
    // Always commit the live CKEditor content to the original cell before
    // anything tears down the editor:
    //   - For Handsontable's own save path (isCancelled=null/false), super
    //     would otherwise call setDataAtCell(this.row, this.col, ...) — but
    //     this.row/col have already been mutated to the new selection, so
    //     super writes to the WRONG cell.
    //   - For our own cancel path (TAB, arrow nav, dblclick of another
    //     cell), super doesn't save at all and the prev CKEditor is about
    //     to be destroyed in the new open() — by the time the late-firing
    //     blur runs, getData() returns "" against the destroyed editor.
    // Use the original row/col parsed from CURRENT_EDITOR_NAME (which was
    // pinned at open time) and the live CKEditor's getData() to write to
    // the correct cell, before any of those mutations happen.
    let preSaved = false;
    if (this._opened && this.CURRENT_EDITOR_NAME) {
      const ck = CKEDITOR.instances[this.CURRENT_EDITOR_NAME];
      const coords = /_(\d+)_(\d+)_\d+$/.exec(this.CURRENT_EDITOR_NAME);
      if (ck && coords && this.instance && this.instance.table) {
        let liveData = null;
        try { liveData = ck.getData(); } catch (_) {}
        // Save whatever's in the editor — including math content. We used
        // to skip math because waitForMathConversion writes the fully-
        // rendered (math + img) form asynchronously, but in the cell-
        // switch race the prev CKEditor gets destroyed before that async
        // path completes and the late blur bails (status='destroyed'),
        // so math content was lost. Saving the raw markup here is a
        // strict improvement over losing it entirely; richTextEnabled
        // cells use htmlRendererSanitize which preserves math markup, so
        // the next render still picks up the WIRIS rendering pipeline.
        if (typeof liveData === 'string') {
          try {
            this.instance.setDataAtCell(parseInt(coords[1], 10), parseInt(coords[2], 10), liveData, 'editedInRichTextEditor');
            preSaved = true;
          } catch (_) {}
        }
      }
    }

    if (this._opened) {
      if (isCancelled) {
        this._opened = false;
        this.state = 'STATE_EDITING';
        super.finishEditing(...arguments);

        const isStillInTable = document.activeElement && document.activeElement.tagName.toLowerCase() === 'body';
        const forceReselectCell = forceReselectCellParam !== undefined ? forceReselectCellParam : false;
        if (isStillInTable || forceReselectCell) {
          this.instance.selectCell(this.row, this.col);
        }
      }

      this.keyPressCharBuffer = null;

      if (this.isInReadOnlyCell()) {
        this.blurMCQ();
      }
    }

    // Call the parent class's finishEditing method. If we already wrote the
    // live editor content above, coerce isCancelled=true so super doesn't
    // also try to saveValue() — BaseEditor uses this.row/this.col which
    // Handsontable has already mutated to the new selection on the implicit
    // save path, which would re-write the prev cell's content to the WRONG
    // (new) cell on top of our correct write.
    super.finishEditing(preSaved ? true : isCancelled, ctrlDown, callback);
  }

  // Check if RichText is enabled
  isRichTextEnabled() {
    return this.instance.getSettings().richTextEnabled === true;
  }

  // Check if there's valid data in key buffer
  hasValidKeyBuffer() {
    return angular.isString(this.keyPressCharBuffer) && this.keyPressCharBuffer.length > 0;
  }

  // Insert key buffer into editor
  insertKeyBufferIntoEditor(editor) {
    if (this.keyPressCharBuffer === null) {
      return;
    }

    editor.insertHtml(this.keyPressCharBuffer);
    this.keyPressCharBuffer = null;
    this.goToLastPosition(editor);
  }

  // Overwrite key buffer into editor
  overwriteKeyBufferIntoEditor(editor) {
    if (this.keyPressCharBuffer === null) {
      return;
    }

    editor.setData(this.keyPressCharBuffer);
    this.keyPressCharBuffer = null;
    this.goToLastPosition(editor);
  }

  // Set cell value and append key buffer
  setCellValueAndAppendKeyBuffer(editor, cellValue) {
    const setDataOpts = {};
    const self = this;

    if (this.hasValidKeyBuffer()) {
      setDataOpts.callback = function () {
        setTimeout(function () {
          self.insertKeyBufferIntoEditor(editor);
        }, 1);
      };
    }

    editor.setData(cellValue, setDataOpts);
  }

  isCaretAtBoundary(editor, keyCode) {
    try {
      const sel = editor.getSelection();
      if (!sel) return false;
      const range = sel.getRanges()[0];
      if (!range || !range.collapsed) return false;
      const atEnd = keyCode === 39 || keyCode === 40;
      return range.checkBoundaryOfElement(editor.editable(), atEnd ? CKEDITOR.END : CKEDITOR.START);
    } catch (e) {
      return false;
    }
  }

  // Place the cursor at the end of the editor content
  goToLastPosition(editor) {
    try {
      const range = editor.createRange();
      const children = editor.element.$.children;
      const lastChild = new CKEDITOR.dom.node(children[children.length - 1]);
      range.moveToElementEditablePosition(lastChild, true);
      range.collapse(false);
      editor.getSelection().selectRanges([range]);
    } catch (e) {
      try {
        const range = editor.createRange();
        range.moveToPosition(range.root, CKEDITOR.POSITION_BEFORE_END);
        editor.getSelection().selectRanges([range]);
      } catch (e2) {
        console.warn('Could not place cursor at the end', e2);
      }
    }
  }

  // Blur all MCQ elements
  blurMCQ() {
    if ($(this.TD).find('input').length > 0) {
      $(this.TD).find('input').blur();
    }
  }
}

HandsontableRichtextEditor._instanceCounter = 0;


export default function registerHandsontableRichtextEditor() {
  // Register the editor with Handsontable
  Handsontable.editors.RichTextEditor = HandsontableRichtextEditor;
  Handsontable.editors.registerEditor('RichTextEditor', HandsontableRichtextEditor);

} 